// The anchored peek (mockup round 084 card 02; ui-map-interface.md §8 "The
// peek").
//
// The peek stands beside the OBJECT, not at the pointer, and a dashed
// leader ties it to the mark. A peek with no mark (open water, a ridge, a
// heat cell) stands at the pointer, and its leader ties the near edge of
// the peek to the centre of the hovered cell (R1875). It opens toward the centre of the clear map:
// the map less the readout line, the dock and the console. So it flips by
// quadrant and never runs under the chrome. It changes side only past the
// guide lines by a hysteresis, so a pointer that wanders over the middle
// does not make it jump. On a telephone it stands clear of the finger:
// above it, or below it when the object stands in the top third.
//
// `placePeek` is pure, in chrome px (R1556). The figures are
// `[presentation.peek]`. `standPeek` writes its answer on the peek host
// and the leader; the season overlay and the arcade chrome both call it
// (R2424).

import { PEEK } from '../../gen/presentation';
import { toChromePx } from '../uiscale';

/** A rect by its four edges. */
export interface Edges { l: number; t: number; r: number; b: number }

/** Which way the peek opens from the object: +1 right or down, -1 left or up. */
export interface PeekSide { sx: 1 | -1; sy: 1 | -1 }

/** Where the peek stands, and its leader from the object to the peek. */
export interface PeekPlace extends PeekSide {
  x: number;
  y: number;
  lead: { x1: number; y1: number; x2: number; y2: number };
}

/** What `placePeek` needs. */
export interface PeekInput {
  /** The object (or the pointer, for open water), in chrome px. */
  at: { x: number; y: number };
  w: number;
  h: number;
  /** The clear map, in chrome px. */
  clear: Edges;
  /** A finger pinned the peek: it stands clear of the finger. */
  finger: boolean;
  /**
   * The map end of the leader when it is not the anchor: the centre of
   * the hovered cell, in chrome px (R1875). The leader then runs to the
   * nearest point of the peek, and has no length when the point is under
   * the peek.
   */
  target?: { x: number; y: number };
  /** The side of the last placement, for the hysteresis. */
  prev?: PeekSide | null;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** The side toward the centre, held by the hysteresis band round it. */
function side(v: number, centre: number, prev: 1 | -1 | undefined): 1 | -1 {
  const h = PEEK.hysteresisPx;
  if (prev === 1 && v < centre + h) return 1;
  if (prev === -1 && v > centre - h) return -1;
  return v < centre ? 1 : -1;
}

/** The leader from a map point to the nearest point of the peek's rect. */
function leadTo(t: { x: number; y: number }, x: number, y: number, w: number, h: number): PeekPlace['lead'] {
  return { x1: t.x, y1: t.y, x2: clamp(t.x, x, x + w), y2: clamp(t.y, y, y + h) };
}

/** Places one peek. */
export function placePeek(p: PeekInput): PeekPlace {
  const { at, w, h, clear: c } = p;
  const g = PEEK.gutterPx;
  const fitX = (x: number) => clamp(x, c.l + g, Math.max(c.l + g, c.r - g - w));
  const fitY = (y: number) => clamp(y, c.t + g, Math.max(c.t + g, c.b - g - h));
  if (p.finger) {
    // Above the finger, or below it when the object is in the top third.
    const below = at.y < c.t + (c.b - c.t) / 3;
    const x = fitX(at.x - w / 2);
    const y = fitY(below ? at.y + PEEK.fingerGapPx : at.y - PEEK.fingerGapPx - h);
    const sy: 1 | -1 = below ? 1 : -1;
    return {
      x, y, sx: 1, sy,
      lead: p.target ? leadTo(p.target, x, y, w, h)
        : { x1: at.x, y1: at.y, x2: clamp(at.x, x, x + w), y2: below ? y : y + h },
    };
  }
  const sx = side(at.x, (c.l + c.r) / 2, p.prev?.sx);
  const sy = side(at.y, (c.t + c.b) / 2, p.prev?.sy);
  const off = PEEK.offsetPx;
  const x = fitX(sx > 0 ? at.x + off : at.x - off - w);
  const y = fitY(sy > 0 ? at.y + off : at.y - off - h);
  return {
    x, y, sx, sy,
    lead: p.target ? leadTo(p.target, x, y, w, h)
      : { x1: at.x, y1: at.y, x2: sx > 0 ? x : x + w, y2: sy > 0 ? y : y + h },
  };
}

/** What `standPeek` needs. Every point is in viewport px. */
export interface PeekStand {
  /** The pointer. */
  pointer: { x: number; y: number };
  /** The object, or null for a thing with no mark: the pointer is then
   *  the anchor. */
  at: { x: number; y: number } | null;
  /** The centre of the hovered cell, or null. A peek with no mark ties its
   *  leader to it (R1875). */
  cell: { x: number; y: number } | null;
  finger: boolean;
  /** The clear map, in chrome px. */
  clear: Edges;
  prev: PeekSide | null;
}

/**
 * Stands a written peek host beside its anchor and draws the dashed leader
 * from the mark, or from the centre of the hovered cell (R1875), to the
 * peek. A peek with neither (a firing solution) needs no leader: the
 * pointer stands at its corner. The answer is the side, for the
 * hysteresis of the next placement.
 */
export function standPeek(tip: HTMLElement, lead: HTMLElement, s: PeekStand): PeekSide {
  // The pointer and the object are in viewport px, and the peek stands
  // in chrome px (R1556).
  const a = s.at ?? s.pointer;
  const place = placePeek({
    at: { x: toChromePx(a.x), y: toChromePx(a.y) },
    w: tip.offsetWidth, h: tip.offsetHeight,
    clear: s.clear, finger: s.finger, prev: s.prev,
    target: s.at === null && s.cell !== null ? { x: toChromePx(s.cell.x), y: toChromePx(s.cell.y) } : undefined,
  });
  tip.style.left = `${place.x}px`;
  tip.style.top = `${place.y}px`;
  const l = place.lead;
  const len = Math.hypot(l.x2 - l.x1, l.y2 - l.y1);
  const led = (s.at !== null || s.cell !== null) && len > 1;
  lead.classList.toggle('on', led);
  if (led) {
    lead.style.left = `${l.x1}px`;
    lead.style.top = `${l.y1}px`;
    lead.style.width = `${len.toFixed(1)}px`;
    lead.style.transform = `rotate(${Math.atan2(l.y2 - l.y1, l.x2 - l.x1).toFixed(4)}rad)`;
  }
  return { sx: place.sx, sy: place.sy };
}
